/**
 * AdminModeration — Modération des comptes utilisateurs (V2.4)
 * Utilise: /api/v1/admin/moderation/queue et /api/v1/admin/moderation/users/{id}
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ShieldCheck, CheckCircle2, XCircle, Clock, AlertTriangle,
  Filter, Search, Loader2, RefreshCw, User as UserIcon, Send,
} from "lucide-react";
import PrivateLayout from "../components/PrivateLayout";
import { api, formatApiError } from "../services/api";

const STATUS_META = {
  pending:   { label: "En attente", color: "#F4B400", bg: "rgba(244,180,0,0.14)", Icon: Clock },
  approved:  { label: "Approuvé",   color: "#009640", bg: "rgba(0,150,64,0.14)",  Icon: CheckCircle2 },
  rejected:  { label: "Rejeté",     color: "#c02a2a", bg: "rgba(192,42,42,0.14)", Icon: XCircle },
  suspended: { label: "Suspendu",   color: "#6b7480", bg: "rgba(107,116,128,0.14)", Icon: AlertTriangle },
};

const ROLE_LABEL = {
  talent: "Talent", employeur: "Employeur", freelance: "Freelance",
  universite: "Université", entrepreneur: "Entrepreneur",
  investisseur: "Investisseur", coach: "Coach", admin: "Admin", "super-admin": "Super Admin",
};

const MODERATABLE_ROLES = ["talent", "employeur", "freelance", "universite"];

export default function AdminModeration() {
  const [items, setItems]   = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState("pending");
  const [roleFilter, setRoleFilter] = useState("");
  const [query, setQuery]   = useState("");
  const [selected, setSelected] = useState(null);
  const [nextStatus, setNextStatus] = useState("approved");
  const [motif, setMotif]   = useState("");
  const [busy, setBusy]     = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const params = new URLSearchParams();
      if (filter !== "all") params.set("status", filter);
      if (roleFilter) params.set("role", roleFilter);
      const { data } = await api.get(`/v1/admin/moderation/queue?${params.toString()}`);
      setItems(data.items || []);
      setCounts(data.counts || {});
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filter, roleFilter]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((u) =>
      (u.name || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q)
    );
  }, [items, query]);

  const openAction = (u, status) => {
    setSelected(u);
    setNextStatus(status);
    setMotif(u.moderation_motif || "");
  };

  const closeAction = () => { if (!busy) { setSelected(null); setMotif(""); } };

  const submitStatus = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.patch(`/v1/admin/moderation/users/${selected.id}`, {
        status: nextStatus,
        motif: motif.trim(),
      });
      toast.success(`Statut mis à jour : ${STATUS_META[nextStatus].label}`);
      closeAction();
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PrivateLayout
      title="Modération des comptes"
      breadcrumbs={[{ label: "Administration" }, { label: "Modération" }]}
      actions={
        <button type="button" onClick={load} className="afr-btn afr-btn--outline afr-btn--sm" data-testid="mod-refresh" disabled={refreshing}>
          {refreshing ? <Loader2 size={14} className="afr-spin" /> : <RefreshCw size={14} />} Rafraîchir
        </button>
      }
    >
      {/* KPIs */}
      <div className="afr-shell__kpis" data-testid="mod-kpis" style={{ marginBottom: "1.2rem" }}>
        {Object.entries(STATUS_META).map(([k, m]) => (
          <div key={k} className="afr-kpi" data-testid={`mod-kpi-${k}`} style={{ border: "1px solid var(--afr-border)", background: "var(--afr-surface)" }}>
            <div className="afr-kpi__icon" style={{ background: `color-mix(in oklab, ${m.color} 14%, transparent)`, color: m.color }}>
              <m.Icon size={18} />
            </div>
            <span className="afr-kpi__label">{m.label}</span>
            <span className="afr-kpi__value">{counts[k] ?? 0}</span>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="afr-card" data-testid="mod-filters" style={{ padding: "0.9rem 1rem", marginBottom: "1rem", display: "flex", gap: "0.6rem", alignItems: "center", flexWrap: "wrap" }}>
        <Filter size={14} style={{ color: "var(--afr-muted)" }} />
        {["pending", "approved", "rejected", "suspended", "all"].map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={`afr-btn afr-btn--sm ${filter === f ? "afr-btn--primary" : "afr-btn--ghost"}`}
            data-testid={`mod-filter-${f}`}
          >
            {f === "all" ? "Tous" : STATUS_META[f].label}
          </button>
        ))}
        <span style={{ opacity: 0.6, margin: "0 0.4rem" }}>·</span>
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          data-testid="mod-role-filter"
          style={{ padding: "0.45rem 0.5rem", borderRadius: 8, border: "1px solid var(--afr-border)", background: "var(--afr-surface)", color: "var(--afr-secondary)", fontFamily: "'Outfit',sans-serif", fontSize: "0.86rem" }}
        >
          <option value="">Tous les rôles</option>
          {MODERATABLE_ROLES.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
        </select>
        <div style={{ flex: 1 }} />
        <div style={{ position: "relative", minWidth: 220 }}>
          <Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--afr-muted)" }} />
          <input type="text" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Nom ou email…" data-testid="mod-search"
            style={{ width: "100%", padding: "0.5rem 0.6rem 0.5rem 2rem", borderRadius: 10, border: "1px solid var(--afr-border)", background: "var(--afr-surface)", color: "var(--afr-secondary)", fontFamily: "'Outfit',sans-serif", fontSize: "0.9rem" }}
          />
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="afr-card" style={{ padding: "3rem", display: "grid", placeItems: "center" }}>
          <Loader2 size={22} className="afr-spin" style={{ color: "var(--afr-primary)" }} />
        </div>
      ) : filtered.length === 0 ? (
        <div className="afr-card" style={{ padding: "2rem", textAlign: "center" }} data-testid="mod-empty">
          <UserIcon size={22} style={{ color: "var(--afr-primary)", marginBottom: "0.4rem" }} />
          <p className="afr-muted" style={{ margin: 0 }}>Aucun utilisateur dans cette catégorie.</p>
        </div>
      ) : (
        <div className="afr-grid" style={{ gridTemplateColumns: "1fr", gap: "0.75rem" }} data-testid="mod-list">
          {filtered.map((u) => {
            const status = u.moderation_status || "approved";
            const meta = STATUS_META[status] || STATUS_META.approved;
            return (
              <article key={u.id} className="afr-card" data-testid={`mod-row-${u.id}`} style={{ padding: "1rem 1.1rem" }}>
                <div style={{ display: "flex", gap: "1rem", alignItems: "center", flexWrap: "wrap" }}>
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", alignItems: "center", marginBottom: "0.3rem" }}>
                      <span style={{ background: meta.bg, color: meta.color, padding: "0.18rem 0.5rem", borderRadius: 999, fontSize: "0.72rem", fontWeight: 700 }}>
                        {meta.label}
                      </span>
                      <span className="afr-muted" style={{ fontSize: "0.78rem" }}>· {ROLE_LABEL[u.role] || u.role}</span>
                    </div>
                    <strong style={{ fontFamily: "'Outfit',sans-serif", color: "var(--afr-secondary)", fontSize: "1rem" }}>{u.name}</strong>
                    <span className="afr-muted" style={{ fontSize: "0.85rem", marginLeft: "0.5rem" }}>{u.email}</span>
                    {u.moderation_motif && (
                      <p className="afr-muted" style={{ margin: "0.3rem 0 0", fontSize: "0.82rem", fontStyle: "italic" }}>
                        Motif : {u.moderation_motif}
                      </p>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
                    <button type="button" className="afr-btn afr-btn--primary afr-btn--sm" onClick={() => openAction(u, "approved")} data-testid={`mod-approve-${u.id}`}>
                      <CheckCircle2 size={13} /> Approuver
                    </button>
                    <button type="button" className="afr-btn afr-btn--outline afr-btn--sm" onClick={() => openAction(u, "rejected")} data-testid={`mod-reject-${u.id}`}>
                      <XCircle size={13} /> Rejeter
                    </button>
                    <button type="button" className="afr-btn afr-btn--outline afr-btn--sm" onClick={() => openAction(u, "suspended")} data-testid={`mod-suspend-${u.id}`}>
                      <AlertTriangle size={13} /> Suspendre
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {/* Action modal */}
      {selected && (
        <div role="dialog" aria-modal="true" onClick={closeAction} data-testid="mod-modal"
          style={{ position: "fixed", inset: 0, background: "rgba(15,42,66,0.55)", display: "grid", placeItems: "center", zIndex: 200, padding: "1rem" }}>
          <div onClick={(e) => e.stopPropagation()} className="afr-card" style={{ background: "var(--afr-surface)", padding: "1.4rem 1.6rem", borderRadius: 18, maxWidth: 520, width: "100%" }}>
            <h3 style={{ margin: "0 0 0.6rem", fontFamily: "'Outfit',sans-serif", color: "var(--afr-secondary)" }}>
              {STATUS_META[nextStatus].label} — {selected.name}
            </h3>
            <p className="afr-muted" style={{ margin: "0 0 0.9rem", fontSize: "0.88rem" }}>
              {selected.email} · {ROLE_LABEL[selected.role] || selected.role}
            </p>
            <label className="afr-muted" style={{ fontSize: "0.78rem", display: "block", marginBottom: "0.3rem" }}>Motif (obligatoire pour rejet/suspension, optionnel sinon)</label>
            <textarea
              value={motif}
              onChange={(e) => setMotif(e.target.value)}
              rows={4}
              placeholder="Ex. profil incomplet, informations non vérifiées, contenu inapproprié…"
              data-testid="mod-motif"
              style={{ width: "100%", padding: "0.6rem 0.75rem", borderRadius: 10, border: "1px solid var(--afr-border)", background: "var(--afr-surface)", fontFamily: "'Outfit',sans-serif", fontSize: "0.9rem", color: "var(--afr-secondary)", resize: "vertical" }}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginTop: "1rem" }}>
              <button type="button" className="afr-btn afr-btn--outline afr-btn--sm" onClick={closeAction} disabled={busy} data-testid="mod-cancel">Annuler</button>
              <button type="button" className="afr-btn afr-btn--primary afr-btn--sm" onClick={submitStatus} disabled={busy} data-testid="mod-submit">
                {busy ? <Loader2 size={14} className="afr-spin" /> : <Send size={14} />}
                Appliquer
              </button>
            </div>
          </div>
        </div>
      )}
    </PrivateLayout>
  );
}
